import React, { useMemo } from 'react';
import { Card, CardContent, Stack, Typography, Box, Divider, useTheme, Tooltip as MuiTooltip } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';
import { TimerOutlined, AssignmentOutlined, EventAvailableOutlined, InfoOutlined } from '@mui/icons-material';
import { Duration } from 'luxon';

const LocalAnalytics = ({ data }) => {
  const theme = useTheme();

  // --- LUXON FORMATTING ---
  // Transforms raw minutes into a "Human Readable" string
  const formattedDuration = useMemo(() => {
    if (!data?.sessionDuration) return '0m';
    
    const dur = Duration.fromObject({ minutes: data.sessionDuration })
      .shiftTo('hours', 'minutes')
      .toObject();
    
    const h = Math.floor(dur.hours || 0);
    const m = Math.floor(dur.minutes || 0);

    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  }, [data?.sessionDuration]);

  // --- CHART DATA ---
  // Stacked Bar only compares Counts (Sessions vs Assessments)
  const chartData = useMemo(() => ([
    {
      name: 'Activity',
      sessions: data?.sessionCount || 0,
      assessments: data?.assessmentCount || 0,
    }
  ]), [data]);

  const StatItem = ({ icon, label, value, color, subValue }) => (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Box sx={{ bgcolor: `${color}15`, color, p: 0.75, borderRadius: 1, display: 'flex' }}>
        {icon}
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 500, lineHeight: 1 }}>
          {label}
        </Typography>
        <Typography variant="h6" sx={{ lineHeight: 1.2, fontWeight: 700 }}>
          {value}
        </Typography>
      </Box>
    </Stack>
  );

  return (
    <Card elevation={1} sx={{ borderRadius: 2, overflow: 'visible' }}>
      <CardContent sx={{ '&:last-child': { pb: 2 } }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="center">
          
          {/* KPI Section */}
          <Stack direction="row" spacing={4} sx={{ flexShrink: 0 }}>
            <StatItem 
              icon={<EventAvailableOutlined fontSize="small" />} 
              label="Sessions" 
              value={data?.sessionCount || 0} 
              color={theme.palette.primary.main} 
            />
            <StatItem 
              icon={<AssignmentOutlined fontSize="small" />} 
              label="Assessments" 
              value={data?.assessmentCount || 0} 
              color={theme.palette.secondary.main} 
            />
            <StatItem 
              icon={<TimerOutlined fontSize="small" />} 
              label="Total Time" 
              value={formattedDuration} 
              color={theme.palette.warning.main} 
            />
          </Stack>

          <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />

          {/* Recharts Distribution Bar */}
          <Box sx={{ width: '100%', height: 65, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography variant="caption" fontWeight="bold" color="text.secondary">
                Activity Ratio
              </Typography>
              <MuiTooltip title="Comparison of completed sessions vs assessments">
                <InfoOutlined sx={{ fontSize: 12, color: 'text.disabled', cursor: 'help' }} />
              </MuiTooltip>
            </Stack>

            <ResponsiveContainer width="100%" height={12}>
              <BarChart layout="vertical" data={chartData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                <XAxis type="number" hide domain={[0, 'dataMax']} />
                <YAxis type="category" dataKey="name" hide />
                <Tooltip 
                  cursor={{ fill: 'transparent' }}
                  contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: theme.shadows[3], fontSize: '12px' }}
                />
                <Bar 
                  dataKey="sessions" 
                  stackId="a" 
                  fill={theme.palette.primary.main} 
                  radius={chartData[0].assessments === 0 ? [10, 10, 10, 10] : [10, 0, 0, 10]} 
                  barSize={12} 
                />
                <Bar 
                  dataKey="assessments" 
                  stackId="a" 
                  fill={theme.palette.secondary.main} 
                  radius={chartData[0].sessions === 0 ? [10, 10, 10, 10] : [0, 10, 10, 0]} 
                  barSize={12} 
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default LocalAnalytics;